import { inject, Injectable } from '@angular/core';
import { FirestoreService } from '../firestore.service';
import { FundBandLevel, PreferenceKey } from '../fund-providers/fund-provider.service';
import { NotFoundException } from '../not-found-exception';

export enum EthicsExposureDecision {
  exposureChoice1 = 'def not',
  exposureChoice2 = 'not really',
  exposureChoice3 = 'ambivalent',
  exposureChoice4 = 'i guess so',
  exposureChoice5 = 'of course',
}

export type Ethics = {
  adultEntertainment: EthicsExposureDecision;
  alcohol: EthicsExposureDecision;
  animalTesting: EthicsExposureDecision;
  civilianWeapons: EthicsExposureDecision;
  gambling: EthicsExposureDecision;
  nuclearPower: EthicsExposureDecision;
  tobacco: EthicsExposureDecision;
  weaponsOfWar: EthicsExposureDecision;
  sustainability: boolean;
}

export type ProfileEthic = {
  id: string;
  updated_at: Date;
  created_at: Date;
  person_id: string;
  ethics_adult_entertainment: string;
  ethics_alcohol: string;
  ethics_animal_testing: string;
  ethics_civilian_weapons: string;
  ethics_gambling: string;
  ethics_nuclear_power: string;
  ethics_tobacco: string;
  ethics_weapons_of_war: string;
  sustainability: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileEthicsService {
  private firestoreService = inject(FirestoreService);

  private collectionPath = 'ProfileEthics';

  async create(profileEthic: ProfileEthic): Promise<string> {
    const newID = this.firestoreService.generateId([this.collectionPath]);
    profileEthic.id = newID;
    await this.firestoreService.set([this.collectionPath, newID], profileEthic);
    return newID;
  }

  async getOne(id: string): Promise<ProfileEthic> {
    return await this.firestoreService.docOnce<ProfileEthic>([this.collectionPath, id]);
  }

  async getByPerson(personId: string): Promise<ProfileEthic[]> {
    return await this.firestoreService.colOnce<ProfileEthic>({
      pathSegments: [this.collectionPath],
      where: ['person_id', '==', personId],
    });
  }

  async getMostCurrentByPerson(personId: string): Promise<ProfileEthic> {
    const profiles = await this.firestoreService.colOnce<ProfileEthic>({
      pathSegments: [this.collectionPath],
      where: ['person_id', '==', personId],
      orderBy: ['created_at', 'desc'],
      limit: 1,
    });
    if (profiles.length > 0) {
      return profiles[0];
    } else {
      throw new NotFoundException(`ProfileEthic not found for ${personId}`);
    }
  }

  async update(id: string, profileEthic: ProfileEthic): Promise<void> {
    return await this.firestoreService.update([this.collectionPath, id], profileEthic);
  }

  convertEthicsDecToSliderValue(dec: EthicsExposureDecision): number {
    switch (dec) {
      case EthicsExposureDecision.exposureChoice1:
        return 0;

      case EthicsExposureDecision.exposureChoice2:
        return 1;

      case EthicsExposureDecision.exposureChoice3:
        return 2;

      case EthicsExposureDecision.exposureChoice4:
        return 3;

      case EthicsExposureDecision.exposureChoice5:
        return 4;

      default:
        return 2;
    }
  }

  convertSliderValueToEthicsDec(sliderValue: number): EthicsExposureDecision {
    switch (sliderValue) {
      case 0:
        return EthicsExposureDecision.exposureChoice1;

      case 1:
        return EthicsExposureDecision.exposureChoice2;

      case 2:
        return EthicsExposureDecision.exposureChoice3;

      case 3:
        return EthicsExposureDecision.exposureChoice4;

      case 4:
        return EthicsExposureDecision.exposureChoice5;

      default:
        return EthicsExposureDecision.exposureChoice2;
    }
  }

  // ===========================================================================
  // V4 Preference Methods
  // ===========================================================================

  /**
   * Get maximum number of top priorities allowed for a selection count.
   * Caps: 0→0, 1-4→1, 5-6→2, 7-8→3, 9-10→4.
   */
  getMaxPriorities(numSelected: number): number {
    if (numSelected <= 0) {return 0;}
    if (numSelected <= 4) {return 1;}
    if (numSelected <= 6) {return 2;}
    if (numSelected <= 8) {return 3;}
    return 4;
  }

  /**
   * Validate that priorities are a valid subset of selected preferences.
   * Checks: all priorities in selected, count within cap, no duplicates.
   */
  validatePriorities(selected: PreferenceKey[], priorities: PreferenceKey[]): boolean {
    if (priorities.length === 0) {return true;}
    if (new Set(priorities).size !== priorities.length) {return false;}
    if (!priorities.every((p) => selected.includes(p))) {return false;}
    return priorities.length <= this.getMaxPriorities(selected.length);
  }

  /**
   * Check to see if a lead's ethic selection matches a fund provider's
   * exposure band ({@link FundBandLevel}).
   * @param userDecision
   * @param fundExposureState
   * @returns True if a match
   */
  isEthicMatchUserDecision(
    userDecision: EthicsExposureDecision,
    fundExposureState: FundBandLevel,
  ): boolean {
    // if the user is ambivalent ro high exposure then we just match
    if (
      userDecision === EthicsExposureDecision.exposureChoice3 ||
      userDecision === EthicsExposureDecision.exposureChoice5
    ) {
      return true;
    }

    switch (userDecision) {
      case EthicsExposureDecision.exposureChoice1:
        if (fundExposureState === FundBandLevel.level1) {
          return true;
        } else {
          return false;
        }

      case EthicsExposureDecision.exposureChoice2:
        if (
          fundExposureState === FundBandLevel.level1 ||
          fundExposureState === FundBandLevel.level2
        ) {
          return true;
        } else {
          return false;
        }

      case EthicsExposureDecision.exposureChoice4:
        if (
          fundExposureState === FundBandLevel.level4 ||
          fundExposureState === FundBandLevel.level5
        ) {
          return false;
        } else {
          return true;
        }
    }
  }
}
